import { createRef, deleteRef, updateRef, type GameMod } from '../api/game-refs';
import {
    modSettingsFromReplay,
    saveModSettings,
    type ModFileSettings,
    type ModSetting,
    type ModSettingPreset,
    type ModSettingsPayload,
} from '../api/mods';
import { state } from '../state';
import { t } from '../i18n';
import { attachAutocomplete } from './autocomplete';
import { getModAuthors, loadModAuthors } from './mod-authors';

// ============================================================
// Модалка мода: добавление и правка (название, автор, описание, настройки).
// Используется в публичном разделе «Моды». У мода один набор настроек:
// имя набора + параметры (имя, значения, дефолт, рандомизируется ли)
// и именованные пресеты (комбинации значений параметров).
// ============================================================

export interface ModEditable {
    id: number;
    name: string;
    author: string | null;
    description: string | null;
    settings: ModSetting | null;
    presets: ModSettingPreset[];
    /** Сколько игр с этим модом — для предупреждения при удалении. */
    games_count?: number;
}

export interface ModModalResult {
    id: number;
    name: string;
    author: string | null;
    description: string | null;
    settings: ModSetting | null;
    presets: ModSettingPreset[];
    /** Мод удалён из модалки — убрать из списка. */
    deleted?: boolean;
}

/** Черновик параметра в редакторе (значения — строкой через запятую). */
interface DraftParam {
    name: string;
    values: string;
    /** Рандомизируется ли параметр по умолчанию. */
    randomize: boolean;
    /** Значение по умолчанию (пусто — не задано). */
    defaultValue: string;
    /** Ветвление: имя родительского параметра (пусто — активен всегда). */
    dependsOn: string;
    /** Значения родителя, при которых параметр активен. */
    dependsValues: string[];
    /** Исключён из рандомизации целиком (правится в окне «Исключения»). */
    disabled: boolean;
    /** Значения, исключённые из розыгрыша (правятся в окне «Исключения»). */
    excludedValues: string[];
}

/** Черновик пресета: имя + значения параметров (имя параметра → значение). */
interface DraftPreset {
    name: string;
    values: Record<string, string>;
}

let initialized = false;

let modal: HTMLElement | null = null;
let form: HTMLFormElement | null = null;
let titleEl: HTMLElement | null = null;
let nameInput: HTMLInputElement | null = null;
let authorInput: HTMLInputElement | null = null;
let descriptionInput: HTMLTextAreaElement | null = null;
let settingsBox: HTMLElement | null = null;
let deleteBtn: HTMLButtonElement | null = null;
/** Число игр с открытым модом (для текста подтверждения удаления). */
let editingGamesCount = 0;

let editingId: number | null = null;
/** Единственный набор настроек мода: черновики параметров. */
let draftParams: DraftParam[] = [];
/** Сразу фокусировать поле имени после добавления параметра. */
let focusNewParam = false;
/** Пресеты настроек (черновики). */
let draftPresets: DraftPreset[] = [];

/** Промис открытой модалки: резолвится сохранённым модом или null при отмене. */
let resolveFn: ((m: ModModalResult | null) => void) | null = null;

// ============================================================
// Автосохранение: правки уходят на сервер сразу, без кнопки «Сохранить».
// Дебаунс — чтобы не долбить сервер на каждый символ.
// ============================================================
const AUTOSAVE_DELAY = 700;

let statusEl: HTMLElement | null = null;
let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
/** Что ещё не ушло на сервер: реквизиты мода и/или настройки. */
let pendingBase = false;
let pendingSettings = false;
/** Текущий запрос сохранения (запросы сериализуем). */
let saveInFlight: Promise<void> | null = null;
/** Растёт при открытии/закрытии модалки — старые ответы сервера игнорируем. */
let session = 0;

/** Последнее подтверждённое сервером состояние — из него собирается результат. */
let savedName = '';
let savedAuthor: string | null = null;
let savedDescription: string | null = null;
let savedSettings: ModSetting | null = null;
let savedPresets: ModSettingPreset[] = [];

/** Кэширует ссылки и вешает обработчики. Безопасно вызывать многократно. */
export function initModModal(): void {
    if (initialized) return;
    initialized = true;

    modal = document.getElementById('mod-modal');
    form = document.getElementById('mod-form') as HTMLFormElement | null;
    titleEl = document.getElementById('mod-modal-title');
    nameInput = document.getElementById('mod-field-name') as HTMLInputElement | null;
    authorInput = document.getElementById('mod-field-author') as HTMLInputElement | null;
    descriptionInput = document.getElementById('mod-field-description') as HTMLTextAreaElement | null;
    settingsBox = document.getElementById('mod-settings-editor');
    statusEl = document.getElementById('mod-save-status');

    // Автоподстановка по уже существующим авторам модов
    if (authorInput) attachAutocomplete(authorInput, getModAuthors);
    void loadModAuthors();

    document.getElementById('mod-modal-close')?.addEventListener('click', () => void requestClose());
    // Кнопка «Готово»: досохраняет введённое и закрывает форму.
    document.getElementById('btn-save-mod')?.addEventListener('click', () => void requestClose());
    deleteBtn = document.getElementById('btn-delete-mod') as HTMLButtonElement | null;
    deleteBtn?.addEventListener('click', () => void deleteMod());
    // Enter в поле — не закрывает форму, а сразу запускает сохранение.
    form?.addEventListener('submit', (e) => {
        e.preventDefault();
        void saveNow();
    });

    // Любая правка полей мода ставит отложенное сохранение.
    nameInput?.addEventListener('input', markBaseDirty);
    authorInput?.addEventListener('input', markBaseDirty);
    descriptionInput?.addEventListener('input', markBaseDirty);
    // Редактор настроек: поля, чекбоксы и селекты (input/change всплывают до контейнера).
    settingsBox?.addEventListener('input', markSettingsDirty);
    settingsBox?.addEventListener('change', markSettingsDirty);

    // Закрытие вкладки/перезагрузка до сохранения — предупреждаем браузером.
    window.addEventListener('beforeunload', onBeforeUnload);

    // Escape закрывает модалку (в фазе перехвата, чтобы не закрыть модалку под ней).
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (!modal || modal.classList.contains('hidden')) return;
        e.stopImmediatePropagation();
        e.preventDefault();
        void requestClose();
    }, true);
}

/** Открывает форму добавления мода. */
export function openAddModModal(): Promise<ModModalResult | null> {
    initModModal();
    if (!modal) return Promise.resolve(null);
    settlePending(null);

    editingId = null;
    deleteBtn?.classList.add('hidden');
    draftParams = [];
    draftPresets = [];
    savedName = '';
    savedAuthor = null;
    savedDescription = null;
    savedSettings = null;
    savedPresets = [];
    resetAutosave();
    if (titleEl) titleEl.textContent = t('mods.form_title');
    if (nameInput) nameInput.value = '';
    if (authorInput) authorInput.value = '';
    if (descriptionInput) descriptionInput.value = '';
    renderSettings();
    void loadModAuthors();
    modal.classList.remove('hidden');
    nameInput?.focus();
    return new Promise((res) => { resolveFn = res; });
}

/** Открывает форму правки мода. */
export function openEditModModal(mod: ModEditable): Promise<ModModalResult | null> {
    initModModal();
    if (!modal) return Promise.resolve(null);
    settlePending(null);

    editingId = mod.id;
    editingGamesCount = mod.games_count ?? 0;
    deleteBtn?.classList.remove('hidden');
    savedSettings = mod.settings ?? null;
    draftParams = (savedSettings?.params ?? []).map((p) => ({
        name: p.name,
        values: p.values.join(', '),
        randomize: p.randomize !== false,
        defaultValue: p.default_value ?? '',
        dependsOn: p.depends_on ?? '',
        dependsValues: [...(p.depends_values ?? [])],
        // Исключения не редактируются здесь — переносим как есть, чтобы не потерять.
        disabled: p.disabled === true,
        excludedValues: [...(p.excluded_values ?? [])],
    }));
    savedPresets = mod.presets ?? [];
    draftPresets = savedPresets.map((p) => ({ name: p.name, values: { ...p.values } }));
    savedName = mod.name;
    savedAuthor = mod.author;
    savedDescription = mod.description;
    resetAutosave();
    if (titleEl) titleEl.textContent = t('mods.form_title_edit');
    if (nameInput) nameInput.value = mod.name;
    if (authorInput) authorInput.value = mod.author ?? '';
    if (descriptionInput) descriptionInput.value = mod.description ?? '';
    renderSettings();
    void loadModAuthors();
    modal.classList.remove('hidden');
    nameInput?.focus();
    return new Promise((res) => { resolveFn = res; });
}

function settlePending(result: ModModalResult | null): void {
    const r = resolveFn;
    resolveFn = null;
    r?.(result);
}

function closeModal(result: ModModalResult | null): void {
    session += 1;
    resetAutosave();
    modal?.classList.add('hidden');
    form?.reset();
    editingId = null;
    draftParams = [];
    draftPresets = [];
    savedSettings = null;
    savedPresets = [];
    settlePending(result);
}

/** Сбрасывает состояние автосохранения (при открытии/закрытии формы). */
function resetAutosave(): void {
    session += 1;
    if (autosaveTimer) {
        clearTimeout(autosaveTimer);
        autosaveTimer = null;
    }
    pendingBase = false;
    pendingSettings = false;
    setStatus('idle');
}

// ============================================================
// Автосохранение
// ============================================================

/** Индикатор в шапке модалки: «Не сохранено» / «Сохранение…» / «Сохранено». */
function setStatus(state: 'idle' | 'pending' | 'saving' | 'saved' | 'error'): void {
    if (!statusEl) return;
    statusEl.classList.remove('is-pending', 'is-saving', 'is-saved', 'is-error');
    if (state === 'idle') {
        statusEl.textContent = '';
        statusEl.title = '';
        return;
    }
    const text = {
        pending: t('mods.autosave_pending'),
        saving: t('common.saving'),
        saved: t('common.saved'),
        error: t('mods.autosave_error'),
    }[state];
    statusEl.classList.add(`is-${state}`);
    statusEl.textContent = text;
    statusEl.title = text;
}

function markBaseDirty(): void {
    pendingBase = true;
    scheduleSave();
}

function markSettingsDirty(): void {
    pendingSettings = true;
    scheduleSave();
}

/** Есть ли что терять: неотправленные правки или идущий запрос. */
function hasUnsavedChanges(): boolean {
    return pendingBase || pendingSettings || saveInFlight !== null;
}

/** Имя мода обязательно: без него сохранять нечего (для нового мода — тем более). */
function hasValidName(): boolean {
    return (nameInput?.value.trim() ?? '') !== '';
}

function scheduleSave(): void {
    if (!pendingBase && !pendingSettings) return;
    setStatus('pending');
    if (autosaveTimer) clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => {
        autosaveTimer = null;
        void saveNow();
    }, AUTOSAVE_DELAY);
}

/**
 * Сохраняет всё, что накопилось, и повторяет попытку, если во время запроса
 * пользователь продолжил печатать. Запросы сериализуются — PATCH настроек
 * заменяет дерево целиком, параллельные вызовы перезаписали бы друг друга.
 */
async function saveNow(): Promise<boolean> {
    for (;;) {
        // Дожидаемся предыдущего запроса (его ошибку обработал инициатор).
        while (saveInFlight) {
            try {
                await saveInFlight;
            } catch {
                /* уже обработано */
            }
        }
        if (!pendingBase && !pendingSettings) return true;
        if (!hasValidName()) return false;
        if (!(await saveOnce())) return false;
    }
}

/** Один проход сохранения того, что накопилось на текущий момент. */
async function saveOnce(): Promise<boolean> {
    const mySession = session;
    const saveBase = pendingBase;
    // У нового мода настроек на сервере ещё нет — пишем их сразу после создания.
    const saveSettings = pendingSettings || editingId === null;
    const targetId = editingId;
    // Снимок значений делаем синхронно: пока идёт запрос, форму могут закрыть.
    const name = nameInput?.value.trim() ?? '';
    const author = authorInput?.value.trim() || null;
    const description = descriptionInput?.value.trim() || null;
    const settingsPayload = draftToPayload();

    pendingBase = false;
    pendingSettings = false;
    setStatus('saving');

    const task = (async (): Promise<void> => {
        let id = targetId;
        if (id === null) {
            const created = await createRef<GameMod>('mods', { name, author, description }, state.token);
            id = created.id;
            if (mySession === session) editingId = created.id;
        } else if (saveBase) {
            await updateRef<GameMod>('mods', id, { name, author, description }, state.token);
        }

        let settings = savedSettings;
        let presets = savedPresets;
        if (saveSettings) {
            const res = await saveModSettings(id, settingsPayload, state.token);
            settings = res.settings;
            presets = res.presets;
        }

        if (mySession !== session) return;
        savedName = name;
        savedAuthor = author;
        savedDescription = description;
        savedSettings = settings;
        savedPresets = presets;
    })();

    saveInFlight = task;
    try {
        await task;
        if (mySession === session && !pendingBase && !pendingSettings) setStatus('saved');
        return true;
    } catch (err) {
        if (mySession !== session) return false;
        console.error('[mod-modal] autosave error:', err);
        // Правки не потерялись — вернём флаги, чтобы повторить попытку.
        pendingBase = pendingBase || saveBase;
        pendingSettings = pendingSettings || saveSettings;
        setStatus('error');
        return false;
    } finally {
        if (saveInFlight === task) saveInFlight = null;
    }
}

/**
 * Закрытие формы: сначала досохраняем введённое, и только если сохранить
 * не удалось — спрашиваем, точно ли закрывать без сохранения.
 */
async function requestClose(): Promise<void> {
    if (autosaveTimer) {
        clearTimeout(autosaveTimer);
        autosaveTimer = null;
    }
    const ok = await saveNow();
    if (!ok) {
        // Без названия сохранять нечего — подсвечиваем поле, а не теряем правки.
        if (!hasValidName()) {
            alert(t('mods.name_required'));
            setStatus('error');
            nameInput?.focus();
            return;
        }
        if (!confirm(t('mods.unsaved_confirm'))) {
            setStatus('error');
            return;
        }
    }
    closeModal(buildResult());
}

/**
 * Удаление мода: у игр сбрасывается mod_id, настройки, пресеты и голоса уходят
 * каскадом; копию (без голосов) сервер кладёт в корзину админки.
 * Отложенное автосохранение снимаем и ждём идущее,
 * чтобы запись не пришла уже после удаления.
 */
async function deleteMod(): Promise<void> {
    const id = editingId;
    if (id === null) return;
    const name = nameInput?.value.trim() || savedName;
    if (!confirm(t('mods.delete_confirm', { name, n: editingGamesCount }))) return;
    if (autosaveTimer) {
        clearTimeout(autosaveTimer);
        autosaveTimer = null;
    }
    // Флаги правок не сбрасываем: если удаление не пройдёт, они досохранятся при закрытии.
    while (saveInFlight) {
        try {
            await saveInFlight;
        } catch {
            /* уже обработано */
        }
    }
    if (deleteBtn) deleteBtn.disabled = true;
    try {
        await deleteRef('mods', id, state.token);
        closeModal({ ...buildResult()!, deleted: true });
    } catch (err) {
        alert(t('mods.delete_error') + (err instanceof Error ? err.message : String(err)));
    } finally {
        if (deleteBtn) deleteBtn.disabled = false;
    }
}

/** Результат для списка модов — только то, что реально лежит в БД. */
function buildResult(): ModModalResult | null {
    if (editingId === null) return null;
    return {
        id: editingId,
        name: savedName,
        author: savedAuthor,
        description: savedDescription,
        settings: savedSettings,
        presets: savedPresets,
    };
}

/** Предупреждение браузера при закрытии вкладки/перезагрузке с несохранёнными правками. */
function onBeforeUnload(e: BeforeUnloadEvent): void {
    if (!hasUnsavedChanges()) return;
    e.preventDefault();
    // Chrome требует вернуть строку (в современных браузерах текст игнорируется).
    e.returnValue = '';
}

/** Перерисовывает редактор единственного набора настроек из черновика. */
function renderSettings(): void {
    if (!settingsBox) return;
    settingsBox.innerHTML = '';

    const group = document.createElement('div');
    group.className = 'mod-setting-group';

    if (draftParams.length === 0) {
        const hint = document.createElement('p');
        hint.className = 'hint';
        hint.textContent = t('mods.settings_empty');
        group.appendChild(hint);
    } else {
        // Параметры-дети показываем вложенными под родителем-переключателем:
        // так структура ветвлений читается сразу.
        const names = new Set(draftParams.map((p) => p.name.trim()).filter(Boolean));
        const childrenByParent = new Map<string, DraftParam[]>();
        const roots: DraftParam[] = [];
        for (const param of draftParams) {
            const dep = param.dependsOn.trim();
            const self = param.name.trim();
            if (dep && dep !== self && names.has(dep)) {
                const list = childrenByParent.get(dep) ?? [];
                list.push(param);
                childrenByParent.set(dep, list);
            } else {
                roots.push(param);
            }
        }

        const rendered = new Set<DraftParam>();
        const renderNode = (param: DraftParam, depth: number): HTMLElement => {
            rendered.add(param);
            const node = document.createElement('div');
            node.className = 'mod-param-node';
            node.appendChild(buildParamCard(param, depth));

            const kids = (childrenByParent.get(param.name.trim()) ?? []).filter((k) => !rendered.has(k));
            if (kids.length > 0) {
                const box = document.createElement('div');
                box.className = 'mod-param-children';
                for (const kid of kids) box.appendChild(renderNode(kid, depth + 1));
                node.appendChild(box);
            }
            return node;
        };

        const tree = document.createElement('div');
        tree.className = 'mod-param-list';
        for (const root of roots) tree.appendChild(renderNode(root, 0));
        // Страховка от циклов (быть не должно): дорисовываем неразобранное корнями.
        for (const param of draftParams) {
            if (!rendered.has(param)) tree.appendChild(renderNode(param, 0));
        }
        group.appendChild(tree);
    }

    const actions = document.createElement('div');
    actions.className = 'mod-setting-actions';
    const addParam = document.createElement('button');
    addParam.type = 'button';
    addParam.className = 'btn-secondary mod-add-param';
    addParam.textContent = t('mods.add_param');
    addParam.title = t('mods.param_add_hint');
    addParam.addEventListener('click', () => {
        addParamDraft('');
    });
    actions.appendChild(addParam);

    // Опции лобби из файла мода: сервер скачает его по реплею из депо Blizzard.
    const fromReplay = document.createElement('button');
    fromReplay.type = 'button';
    fromReplay.className = 'btn-secondary mod-import-replay';
    fromReplay.textContent = t('mods.import_replay');
    fromReplay.title = t('mods.import_replay_hint');
    const replayInput = document.createElement('input');
    replayInput.type = 'file';
    replayInput.accept = '.SC2Replay';
    replayInput.hidden = true;
    replayInput.addEventListener('change', () => {
        const file = replayInput.files?.[0];
        replayInput.value = '';
        if (file) void importSettingsFromReplay(file, fromReplay);
    });
    fromReplay.addEventListener('click', () => replayInput.click());
    actions.append(fromReplay, replayInput);
    group.appendChild(actions);

    settingsBox.appendChild(group);
    settingsBox.appendChild(buildPresetsEditor());

    // После добавления параметра сразу даём фокус его полю имени
    if (focusNewParam) {
        focusNewParam = false;
        const inputs = settingsBox.querySelectorAll<HTMLInputElement>('.mod-param-name-input');
        for (let i = inputs.length - 1; i >= 0; i--) {
            const input = inputs[i];
            if (input && !input.value.trim()) {
                input.focus();
                break;
            }
        }
    }
}

/** Заменяет параметры опциями лобби мода, на котором сыгран реплей. */
async function importSettingsFromReplay(file: File, button: HTMLButtonElement): Promise<void> {
    const mySession = session;
    button.disabled = true;
    button.textContent = t('mods.import_replay_loading');
    let candidates: ModFileSettings[];
    try {
        candidates = (await modSettingsFromReplay(file, state.token)).candidates;
    } catch (err) {
        alert(t('mods.import_replay_error') + (err instanceof Error ? err.message : String(err)));
        return;
    } finally {
        button.disabled = false;
        button.textContent = t('mods.import_replay');
    }
    // Пока шёл запрос, окно закрыли или открыли другой мод.
    if (mySession !== session) return;

    if (candidates.length === 0) {
        alert(t('mods.import_replay_none'));
        return;
    }
    // Свои опции лобби бывают и у карты — тогда спрашиваем по очереди.
    const picked = candidates.length === 1
        ? candidates[0]
        : candidates.find((c) => confirm(t('mods.import_replay_pick', { name: c.name ?? '?' })));
    if (!picked) return;
    if (draftParams.some((p) => p.name.trim()) && !confirm(t('mods.import_replay_replace', {
        name: picked.name ?? '?',
        params: picked.params.map((p) => p.name).join(', '),
    }))) return;

    // Новый мод без названия — берём название из файла мода.
    if (nameInput && !nameInput.value.trim() && picked.name) {
        nameInput.value = picked.name;
        markBaseDirty();
    }

    // Рандомизацию и исключения модератора по совпавшему имени сохраняем.
    const previous = new Map(draftParams.map((p) => [p.name.trim(), p]));
    draftParams = picked.params.map((p) => {
        const prev = previous.get(p.name);
        return {
            name: p.name,
            values: p.values.join(', '),
            randomize: prev?.randomize ?? true,
            defaultValue: p.default_value ?? '',
            dependsOn: p.depends_on ?? '',
            dependsValues: p.depends_values,
            disabled: prev?.disabled ?? false,
            excludedValues: (prev?.excludedValues ?? []).filter((v) => p.values.includes(v)),
        };
    });
    markSettingsDirty();
    renderSettings();
}

/** Добавляет новый параметр (с ветвлением от указанного родителя, если задан). */
function addParamDraft(parentName: string): void {
    const parent = draftParams.find((p) => p.name.trim() === parentName);
    focusNewParam = true;
    draftParams.push({
        name: '',
        values: '',
        randomize: true,
        defaultValue: '',
        dependsOn: parent ? parent.name.trim() : '',
        // Отмечаем все значения родителя, чтобы ветвление сразу было валидным
        dependsValues: parent ? parseValues(parent.values) : [],
        disabled: false,
        excludedValues: [],
    });
    markSettingsDirty();
    renderSettings();
}

/** Карточка параметра: название, значения, дефолт и ветвление. */
function buildParamCard(param: DraftParam, depth: number): HTMLElement {
    const card = document.createElement('div');
    card.className = 'mod-param-card' + (depth > 0 ? ' mod-param-card--child' : '');

    const head = document.createElement('div');
    head.className = 'mod-param-card-head';

    const index = document.createElement('span');
    index.className = 'mod-param-index';
    index.textContent = String(draftParams.indexOf(param) + 1);
    head.appendChild(index);

    const paramName = document.createElement('input');
    paramName.type = 'text';
    paramName.className = 'mod-param-name-input';
    paramName.value = param.name;
    paramName.placeholder = t('mods.param_name_placeholder');
    paramName.addEventListener('input', () => {
        const oldName = param.name.trim();
        param.name = paramName.value;
        const newName = param.name.trim();
        // Переименование переносим в зависимости других параметров,
        // чтобы ветвление не потерялось.
        if (oldName && newName && oldName !== newName) {
            for (const p of draftParams) {
                if (p.dependsOn.trim() === oldName) p.dependsOn = newName;
            }
        }
    });
    head.appendChild(paramName);

    // Метка зависимости: при каком значении родителя параметр активен
    const parent = findParent(param);
    if (!parent && param.dependsOn.trim()) {
        // Родитель исчез (переименован/удалён) — ветвление сбрасываем
        param.dependsOn = '';
        param.dependsValues = [];
    }
    if (parent) {
        const pill = document.createElement('span');
        pill.className = 'mod-param-cond-pill';
        pill.textContent = t('mods.param_depends_short', {
            param: parent.name.trim(),
            values: param.dependsValues.length > 0 ? param.dependsValues.join(' / ') : '—',
        });
        pill.title = t('mods.param_depends_hint');
        head.appendChild(pill);
    }

    // Сколько параметров зависят от этого — это «переключатель»
    const selfName = param.name.trim();
    const childCount = selfName
        ? draftParams.filter((p) => p !== param && p.dependsOn.trim() === selfName).length
        : 0;
    if (childCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'mod-param-switch-badge';
        badge.textContent = t('mods.param_branch_count', { n: childCount });
        badge.title = t('mods.param_branch_count_hint');
        head.appendChild(badge);
    }

    // Галочка «рандомизировать по умолчанию».
    const randomizeLabel = document.createElement('label');
    randomizeLabel.className = 'mod-param-random';
    randomizeLabel.title = t('mods.param_randomize_hint');

    const randomizeBox = document.createElement('input');
    randomizeBox.type = 'checkbox';
    randomizeBox.checked = param.randomize;
    randomizeBox.addEventListener('change', () => { param.randomize = randomizeBox.checked; });

    const randomizeText = document.createElement('span');
    randomizeText.textContent = t('mods.param_randomize');

    randomizeLabel.append(randomizeBox, randomizeText);
    head.appendChild(randomizeLabel);

    const removeParam = document.createElement('button');
    removeParam.type = 'button';
    removeParam.className = 'admin-ref-btn admin-ref-btn--delete';
    removeParam.textContent = '✕';
    removeParam.title = t('mods.delete_param');
    removeParam.addEventListener('click', () => {
        // Снимаем ветвления, которые ссылались на удаляемый параметр
        const removedName = param.name.trim();
        if (removedName) {
            for (const p of draftParams) {
                if (p.dependsOn.trim() === removedName) {
                    p.dependsOn = '';
                    p.dependsValues = [];
                }
            }
        }
        draftParams.splice(draftParams.indexOf(param), 1);
        markSettingsDirty();
        renderSettings();
    });
    head.appendChild(removeParam);

    card.appendChild(head);

    const paramValues = document.createElement('input');
    paramValues.type = 'text';
    paramValues.value = param.values;
    paramValues.placeholder = t('mods.param_values_placeholder');
    paramValues.title = t('mods.param_values_placeholder');
    paramValues.addEventListener('input', () => { param.values = paramValues.value; });

    const paramDefault = document.createElement('input');
    paramDefault.type = 'text';
    paramDefault.value = param.defaultValue;
    paramDefault.placeholder = t('mods.param_default_placeholder');
    paramDefault.title = t('mods.param_default_hint');
    paramDefault.addEventListener('input', () => { param.defaultValue = paramDefault.value; });

    const fields = document.createElement('div');
    fields.className = 'mod-param-fields';
    fields.append(
        buildParamField(t('mods.param_field_values'), paramValues),
        buildParamField(t('mods.param_field_default'), paramDefault),
    );
    card.appendChild(fields);

    card.appendChild(buildBranchRow(param, parent));
    return card;
}

/** Поле с подписью сверху (подписи читаются лучше плейсхолдеров). */
function buildParamField(labelText: string, input: HTMLInputElement): HTMLElement {
    const label = document.createElement('label');
    label.className = 'mod-param-field';

    const caption = document.createElement('span');
    caption.className = 'mod-param-field-label';
    caption.textContent = labelText;

    label.append(caption, input);
    return label;
}

/** Родительский параметр, если он есть и существует в черновике. */
function findParent(param: DraftParam): DraftParam | null {
    const dep = param.dependsOn.trim();
    if (!dep || dep === param.name.trim()) return null;
    return draftParams.find((p) => p.name.trim() === dep) ?? null;
}

/** Параметры, которые (транзитивно) зависят от указанного. */
function dependentNames(name: string): Set<string> {
    const out = new Set<string>();
    if (!name) return out;

    let changed = true;
    while (changed) {
        changed = false;
        for (const p of draftParams) {
            const pName = p.name.trim();
            const dep = p.dependsOn.trim();
            if (!pName || !dep || out.has(pName)) continue;
            if (dep === name || out.has(dep)) {
                out.add(pName);
                changed = true;
            }
        }
    }

    return out;
}

/**
 * Блок ветвления параметра: выбор родителя-переключателя и его значений.
 * Пусто — параметр активен всегда.
 */
function buildBranchRow(param: DraftParam, parent: DraftParam | null): HTMLElement {
    const row = document.createElement('div');
    row.className = 'mod-param-branch';

    const top = document.createElement('div');
    top.className = 'mod-param-branch-top';

    const label = document.createElement('span');
    label.className = 'mod-param-branch-label';
    label.textContent = t('mods.param_depends_label');
    label.title = t('mods.param_depends_hint');
    top.appendChild(label);

    const selfName = param.name.trim();
    // На своих потомков ссылаться нельзя — это замкнуло бы ветвление в цикл.
    const forbidden = dependentNames(selfName);
    const parents = draftParams.filter((p) => {
        const name = p.name.trim();
        return name && name !== selfName && !forbidden.has(name);
    });

    const select = document.createElement('select');
    select.className = 'mod-param-branch-select';
    select.title = t('mods.param_depends_hint');

    const none = document.createElement('option');
    none.value = '';
    none.textContent = t('mods.param_depends_always');
    select.appendChild(none);
    for (const p of parents) {
        const name = p.name.trim();
        const opt = document.createElement('option');
        opt.value = name;
        opt.textContent = name;
        select.appendChild(opt);
    }
    select.value = parent ? parent.name.trim() : '';
    select.addEventListener('change', () => {
        const chosen = select.value;
        param.dependsOn = chosen;
        const picked = draftParams.find((p) => p.name.trim() === chosen);
        // При выборе переключателя сразу отмечаем все его значения,
        // чтобы ветвление было валидным без лишних кликов.
        param.dependsValues = picked ? parseValues(picked.values) : [];
        renderSettings();
    });
    top.appendChild(select);

    // Быстрое добавление вложенного параметра под этим параметром
    const addChild = document.createElement('button');
    addChild.type = 'button';
    addChild.className = 'btn-secondary mod-param-add-child';
    addChild.textContent = t('mods.param_branch_add');
    addChild.title = t('mods.param_branch_add_hint');
    addChild.disabled = !selfName;
    addChild.addEventListener('click', () => addParamDraft(selfName));
    top.appendChild(addChild);

    row.appendChild(top);

    if (parent) {
        const values = parseValues(parent.values);
        const chipsRow = document.createElement('div');
        chipsRow.className = 'mod-param-branch-values';

        // Предупреждение «не выбрано ни одно значение» — ниже чипов
        const warnEl = document.createElement('span');
        warnEl.className = 'mod-param-branch-warn' + (param.dependsValues.length > 0 ? ' hidden' : '');
        warnEl.textContent = t('mods.param_branch_warn');

        const caption = document.createElement('span');
        caption.className = 'mod-param-branch-values-label';
        caption.textContent = t('mods.param_branch_values_label');
        chipsRow.appendChild(caption);

        for (const value of values) {
            const chip = document.createElement('button');
            chip.type = 'button';
            const active = param.dependsValues.includes(value);
            chip.className = 'mod-branch-chip' + (active ? ' is-active' : '');
            chip.textContent = value;
            chip.title = t('mods.param_depends_values_hint');
            chip.addEventListener('click', () => {
                if (param.dependsValues.includes(value)) {
                    param.dependsValues = param.dependsValues.filter((v) => v !== value);
                } else {
                    param.dependsValues.push(value);
                }
                chip.classList.toggle('is-active', param.dependsValues.includes(value));
                warnEl.classList.toggle('hidden', param.dependsValues.length > 0);
                markSettingsDirty();
            });
            chipsRow.appendChild(chip);
        }

        if (values.length === 0) {
            const hint = document.createElement('span');
            hint.className = 'hint';
            hint.textContent = t('mods.presets_no_params');
            chipsRow.appendChild(hint);
        }

        row.appendChild(chipsRow);
        row.appendChild(warnEl);
    }

    return row;
}

/** Редактор пресетов настроек: имя + значения по каждому параметру. */
function buildPresetsEditor(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'mod-presets';

    const head = document.createElement('div');
    head.className = 'mod-presets-head';

    const title = document.createElement('span');
    title.textContent = t('mods.presets_title');
    head.appendChild(title);

    const addPreset = document.createElement('button');
    addPreset.type = 'button';
    addPreset.className = 'btn-secondary mod-add-param';
    addPreset.textContent = t('mods.add_preset');
    addPreset.addEventListener('click', () => {
        draftPresets.push({ name: '', values: {} });
        markSettingsDirty();
        renderSettings();
    });
    head.appendChild(addPreset);
    wrap.appendChild(head);

    const hint = document.createElement('p');
    hint.className = 'hint';
    hint.textContent = t('mods.presets_hint');
    wrap.appendChild(hint);

    // Параметры, по которым можно задать значения в пресете.
    const params = draftParams
        .map((p) => ({ name: p.name.trim(), values: parseValues(p.values) }))
        .filter((p) => p.name && p.values.length > 0);

    if (draftPresets.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'hint';
        empty.textContent = t('mods.presets_empty');
        wrap.appendChild(empty);
        return wrap;
    }

    for (const [pi, preset] of draftPresets.entries()) {
        const row = document.createElement('div');
        row.className = 'mod-preset';

        const rowHead = document.createElement('div');
        rowHead.className = 'mod-preset-head';

        const presetName = document.createElement('input');
        presetName.type = 'text';
        presetName.value = preset.name;
        presetName.placeholder = t('mods.preset_name_placeholder');
        presetName.addEventListener('input', () => { preset.name = presetName.value; });
        rowHead.appendChild(presetName);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'admin-ref-btn admin-ref-btn--delete';
        remove.textContent = '✕';
        remove.title = t('mods.delete_preset');
        remove.addEventListener('click', () => {
            draftPresets.splice(pi, 1);
            markSettingsDirty();
            renderSettings();
        });
        rowHead.appendChild(remove);
        row.appendChild(rowHead);

        if (params.length === 0) {
            const hintParams = document.createElement('p');
            hintParams.className = 'hint';
            hintParams.textContent = t('mods.presets_no_params');
            row.appendChild(hintParams);
        } else {
            const values = document.createElement('div');
            values.className = 'mod-preset-values';

            for (const param of params) {
                const label = document.createElement('label');
                label.className = 'mod-preset-value';

                const caption = document.createElement('span');
                caption.textContent = param.name;
                label.appendChild(caption);

                const select = document.createElement('select');
                const empty = document.createElement('option');
                empty.value = '';
                empty.textContent = '—';
                select.appendChild(empty);
                for (const v of param.values) {
                    const opt = document.createElement('option');
                    opt.value = v;
                    opt.textContent = v;
                    select.appendChild(opt);
                }
                const current = preset.values[param.name] ?? '';
                select.value = param.values.includes(current) ? current : '';
                select.addEventListener('change', () => {
                    if (select.value) preset.values[param.name] = select.value;
                    else delete preset.values[param.name];
                });

                label.appendChild(select);
                values.appendChild(label);
            }

            row.appendChild(values);
        }

        wrap.appendChild(row);
    }

    return wrap;
}

/** «a, b; c» → ['a','b','c'] (без пустых и дублей). */
function parseValues(raw: string): string[] {
    return [...new Set(
        raw.split(/[,;\n]/).map((v) => v.trim()).filter(Boolean)
    )];
}

/** Приводит черновик к payload для сервера (пустые названия пропускаются). */
function draftToPayload(): ModSettingsPayload {
    const params: ModSettingsPayload['params'] = [];
    for (const param of draftParams) {
        const paramName = param.name.trim();
        if (!paramName) continue;
        const values = parseValues(param.values);
        const def = param.defaultValue.trim();
        // Дефолт всегда должен быть среди значений — если его там нет, добавляем.
        if (def && !values.includes(def)) values.push(def);
        const dependsOn = param.dependsOn.trim();
        params.push({
            name: paramName,
            values,
            randomize: param.randomize !== false,
            default_value: def || null,
            depends_on: dependsOn || null,
            depends_values: dependsOn ? [...param.dependsValues] : [],
            disabled: param.disabled,
            // Исключения переносим только для оставшихся значений
            excluded_values: param.excludedValues.filter((v) => values.includes(v)),
        });
    }

    // Ветвление: родитель должен существовать (после переименований/удалений),
    // значения — из его списка.
    const paramByName = new Map(params.map((p) => [p.name, p]));
    for (const param of params) {
        if (!param.depends_on) {
            param.depends_values = [];
            continue;
        }
        const parent = paramByName.get(param.depends_on);
        if (!parent) {
            param.depends_on = null;
            param.depends_values = [];
            continue;
        }
        param.depends_values = param.depends_values.filter((v) => parent.values.includes(v));
        if (param.depends_values.length === 0) param.depends_on = null;
    }

    // Пресеты: оставляем только параметры, которые реально есть в наборе,
    // и только допустимые значения; пустые имена отбрасываем.
    const byName = new Map(params.map((p) => [p.name, p]));
    const presets: ModSettingsPayload['presets'] = [];
    for (const preset of draftPresets) {
        const presetName = preset.name.trim();
        if (!presetName) continue;
        const values: Record<string, string> = {};
        for (const [paramName, value] of Object.entries(preset.values)) {
            const param = byName.get(paramName);
            if (param && param.values.includes(value)) values[paramName] = value;
        }
        presets.push({ name: presetName, values });
    }

    return { params, presets };
}
